
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { lazy, Suspense, useEffect } from "react";
import { Helmet } from "react-helmet";
import { CtaSection } from "@/components/ui/cta-section";

// Import components above the fold directly
import { HeroSection } from "@/components/specializations/microsoft365engineering/HeroSection";
import { ServiceCards } from "@/components/specializations/microsoft365engineering/ServiceCards";

// Lazy load components below the fold
const LazyPlatformValueSection = lazy(() => 
  import("@/components/specializations/microsoft365engineering/PlatformValueSection").then(
    module => ({ default: module.PlatformValueSection })
  )
);

const LazySuccessStoriesSection = lazy(() => 
  import("@/components/specializations/microsoft365engineering/SuccessStoriesSection").then(
    module => ({ default: module.SuccessStoriesSection })
  )
);

// Loading component for better user experience
const SectionLoader = ({ height = "py-16" }: { height?: string }) => (
  <div className={`${height} px-4`}>
    <div className="max-w-6xl mx-auto">
      <div className="h-40 bg-gray-100 rounded-xl animate-pulse"></div>
    </div>
  </div>
);

// New component for challenges section
const ChallengesSection = () => {
  const challenges = [
    {
      title: "Inefficient Tenant Architecture",
      description: "Poorly designed Microsoft 365 tenants create administrative bottlenecks, security gaps, and limit your ability to scale efficiently."
    },
    {
      title: "Underutilized Platform Capabilities",
      description: "Most organizations use less than 40% of their Microsoft 365 capabilities, missing significant value from their investment."
    },
    {
      title: "Integration & Automation Gaps",
      description: "Disjointed Microsoft 365 services and manual workflows create friction that reduces productivity and increases human error."
    },
    {
      title: "User Adoption Barriers",
      description: "Technical complexity and inconsistent configurations lead to low user adoption and shadow IT usage."
    },
    {
      title: "Security & Compliance Risks",
      description: "Misconfigured permissions, data sprawl, and inconsistent governance increase your security exposure and compliance risks."
    },
    {
      title: "Lack of Strategic Roadmap",
      description: "Without a clear Microsoft 365 engineering roadmap, organizations struggle with prioritization and long-term platform success."
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl text-center mb-4">
            Common Microsoft 365 Engineering Challenges
          </h2>
          
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Organizations often struggle with these critical Microsoft 365 platform challenges that impact productivity, security, and ROI.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <div key={index} className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{challenge.title}</h3>
                <p className="text-gray-600">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// New FAQ section
const FaqSection = () => {
  const faqs = [
    {
      question: "How long does a Microsoft 365 architecture project typically take?",
      answer: "The timeline depends on your organization's size and complexity. A targeted architecture solution for a midsized business typically takes 4-6 weeks, while enterprise-wide implementations may take 8-12 weeks or longer. We can also implement in phases to address your most critical needs first."
    },
    {
      question: "How does your approach differ from Microsoft's FastTrack program?",
      answer: "While FastTrack provides basic deployment guidance, our engineering approach goes far beyond basic setup. We design custom architectures specifically for your business requirements, integrate with your existing systems, implement advanced security controls, and provide ongoing optimization to maximize your Microsoft 365 investment."
    },
    {
      question: "Can you work with our existing IT team?",
      answer: "Absolutely. We excel at collaborative engagements with internal IT teams, providing specialized Microsoft 365 expertise while transferring knowledge to your team. We can augment your capabilities or lead specific initiatives based on your needs."
    },
    {
      question: "Will this disrupt our day-to-day operations?",
      answer: "We deploy changes using a staged approach with minimal disruption. Most architecture improvements are implemented behind the scenes with carefully planned cutover periods. Our clients typically experience zero downtime for critical services during the transition."
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl text-center mb-4">
            Frequently Asked Questions
          </h2>
          
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Answers to common questions about our Microsoft 365 engineering approach
          </p>
          
          <div className="space-y-8">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Microsoft365Engineering = () => {
  const pageTitle = "Microsoft 365 Engineering & Architecture | edgefi";
  const pageDescription = "Build a robust, scalable Microsoft 365 environment designed for your specific needs. Our engineering expertise transforms complex technology into a business advantage.";
  const pageUrl = window.location.origin + "/specializations/microsoft-365-engineering";

  useEffect(() => {
    // Add Service schema
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Microsoft 365 Engineering & Architecture",
      "provider": {
        "@type": "Organization",
        "name": "edgefi",
        "url": window.location.origin
      },
      "serviceType": "Microsoft 365 Engineering",
      "description": "Build a robust, scalable Microsoft 365 environment with our engineering and architecture expertise.",
      "offers": {
        "@type": "Offer",
        "description": "Microsoft 365 Architecture Design, Tenant Engineering, Automation & Integration"
      },
      "areaServed": {
        "@type": "Country",
        "name": "United States"
      },
      "url": pageUrl
    });
    document.head.appendChild(serviceScript);
    
    // Add BreadcrumbList schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": window.location.origin
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Specializations",
          "item": `${window.location.origin}/specializations`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Microsoft 365 Engineering",
          "item": pageUrl
        }
      ]
    });
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(serviceScript);
      document.head.removeChild(breadcrumbScript);
    };
  }, [pageUrl]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="Microsoft 365 engineering, Microsoft 365 architecture, Microsoft 365 tenant design, Microsoft 365 automation, SharePoint architecture, Teams design" />
        <link rel="canonical" href={pageUrl} />
        
        {/* Geo-targeting meta tags */}
        <meta name="geo.region" content="US" />
        <meta name="geo.position" content="37.09024;-95.712891" />
        <meta name="ICBM" content="37.09024, -95.712891" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${window.location.origin}/og-image.png`} />
        <meta property="og:image:alt" content="edgefi Microsoft 365 engineering specialists" />
        <meta property="og:site_name" content="edgefi" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${window.location.origin}/og-image.png`} />
        <meta name="twitter:image:alt" content="edgefi Microsoft 365 engineering specialists" />
        
        {/* Additional SEO tags */}
        <meta name="author" content="edgefi" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <Navbar />
      
      <main>
        {/* Above the fold content loads immediately */}
        <HeroSection />
        <ServiceCards />
        
        {/* Add new challenges section */}
        <ChallengesSection />
        
        {/* Below the fold content loads lazily */}
        <Suspense fallback={<SectionLoader />}>
          <LazyPlatformValueSection />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <LazySuccessStoriesSection />
        </Suspense>
        
        {/* Add new FAQ section */}
        <FaqSection />
        
        {/* Add focused CTA */}
        <CtaSection 
          title="Ready to Transform Your Microsoft 365 Environment?" 
          description="Schedule a consultation with our Microsoft 365 engineering experts to discuss your specific needs and challenges."
          buttonText="Schedule Your Consultation"
          buttonVariant="cta"
          bgColor="gradient"
          textColor="white"
          useContactModal={true}
          modalTitle="Microsoft 365 Engineering Consultation"
          modalDescription="Tell us about your Microsoft 365 challenges and goals."
        />
      </main>
      
      <Footer />
    </div>
  );
};

export default Microsoft365Engineering;
